/**
 * SCHEDULE — the store half (migration 3071; DESIGN-CHANGELOG 2026-10-14
 * "SCHEDULE ships into both mirrors"). The prototype's shells own this as
 * `sched` + the `sc*` family (`design/Games Tonight.dc.html` ~2500-2580); the
 * rules live server-side and this slice only asks.
 *
 * NAMES. The prototype's prefix is `sc`, which this store already spends on
 * MY SCORES (`scId`, `openScore`), so the schedule's state is `sch*`.
 *
 * NOT OPTIMISTIC, the round's reasoning: every write is one RPC that returns
 * the night as it stands afterwards, and the answer is installed whole. The
 * refusals (`already nominated for this night`, `only the nominator can
 * remove it`) are the feature's rules, not network noise, and a night is a
 * few hundred bytes. `schBusy` guards a double press while one is open.
 *
 * WHAT IS LOADED. `sched` holds the nights the month grid last asked for
 * (`schSpan`), plus any night opened outside it. Nothing is fetched at boot:
 * the tab asks when it is shown, and a realtime `sched.changed` refetches the
 * span only once something has been shown.
 */

import type { StateCreator } from 'zustand';

import { R_ARM_MS } from '../../lib/round.ts';
import { scDayTxt, scMonthSpan, scOnWheel } from '../../lib/schedule.ts';
import type { SchedMap, SchedNom } from '../../lib/schedule.ts';
import { RepoError } from '../../repo/Repository.ts';
import type { QdStore } from '../types.ts';

export type SchedArm = '' | 'rm';
export type SchedTierTab = 'all' | 's' | 'a' | 'b' | 'c';

export interface ScheduleSlice {
  /** Nights by key; an empty night is absent. */
  sched: SchedMap;
  /** The span the month grid last fetched, or null before the tab was shown. */
  schSpan: { from: string; to: string } | null;
  /** The month on show: full year, 0-based month. */
  schY: number;
  schM: number;
  /** The open night's key ('' = none). */
  schDay: string;
  /** The game modal's appid (0 = closed). */
  schGame: number;
  /** EDIT RANGE is open inside the game modal. */
  schEdit: boolean;
  /** + NOMINATE is open. */
  schNomOpen: boolean;
  schArm: SchedArm;
  /** The picked game in NOMINATE (0 = none). */
  schPick: number;
  /** The slider: hours of the night clock (9..24), null = open. */
  schFrom: number | null;
  schTo: number | null;
  /** NOMINATE's search and tier tab. */
  schQ: string;
  schTier: SchedTierTab;
  schBusy: boolean;

  /** Fetch the month on show (and the open night if it sits outside it). */
  refreshSched: () => Promise<void>;
  /** Fetch one night, wherever it sits — the realtime answer for a night opened outside the month. */
  schLoadNight: (k: string) => Promise<void>;
  schMonthGo: (d: number) => void;
  schOpenDay: (k: string) => void;
  schBack: () => void;
  schNomStart: () => void;
  schNomClose: () => void;
  schNomAdd: () => Promise<void>;
  /** My quarter on someone else's nomination: on, or back off. */
  schQuarter: (id: number) => Promise<void>;
  schOpenGame: (id: number) => void;
  schCloseGame: () => void;
  schEditTgl: () => void;
  schEditSave: () => Promise<void>;
  /** Arms on the first press, takes it off the night on the second. */
  schRemove: () => Promise<void>;
  /** START GAME NIGHT from tonight's page. */
  schStart: () => Promise<void>;
  schSetPick: (id: number) => void;
  schSetRange: (from: number | null, to: number | null) => void;
  schSetQ: (q: string) => void;
  schSetTier: (t: SchedTierTab) => void;
  schArmSet: (key: SchedArm) => void;
}

/**
 * The crew-scoped half, blanked on a crew switch and a sign-out — the
 * calendar AND everything that pointed into it. Spread by `clearedCrewState`
 * and `signOut`.
 */
export const SCHEDULE_CLEARED = {
  sched: {} as SchedMap,
  schSpan: null as { from: string; to: string } | null,
  schDay: '',
  schGame: 0,
  schEdit: false,
  schNomOpen: false,
  schArm: '' as SchedArm,
  schPick: 0,
  schBusy: false,
};

/** Install one night's answer: replaced whole, or removed when empty. */
function withNight(m: SchedMap, k: string, noms: SchedNom[]): SchedMap {
  const out = { ...m };
  if (noms.length) out[k] = noms;
  else delete out[k];
  return out;
}

export const createScheduleSlice: StateCreator<QdStore, [], [], ScheduleSlice> = (set, get) => {
  let armTimer: ReturnType<typeof setTimeout> | null = null;
  let armAway: ((ev: Event) => void) | null = null;
  /** Bumps on every fetch, so a slow answer for a month already left cannot land over the next. */
  let fetchSeq = 0;

  const disarmListener = (): void => {
    if (armAway && typeof document !== 'undefined') document.removeEventListener('click', armAway, true);
    armAway = null;
  };

  const top = (): void => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  };

  /**
   * One RPC, one answer, one toast on refusal — the round's `call()`. The
   * server's reason is the copy (`ALREADY NOMINATED FOR THIS NIGHT`); a
   * network-class failure gets the generic line instead of an invented rule,
   * and the night is refetched either way, because a refusal usually means our
   * copy is stale.
   */
  const write = async (night: string, fn: () => Promise<SchedNom[]>, after?: () => void): Promise<boolean> => {
    if (get().schBusy) return false;
    set({ schBusy: true });
    try {
      const noms = await fn();
      set({ sched: withNight(get().sched, night, noms) });
      after?.();
      return true;
    } catch (e) {
      const known = e instanceof RepoError && (e.code === 'FORBIDDEN' || e.code === 'NOT_FOUND');
      get().toast(known && e.message ? '✕ ' + e.message.toUpperCase() : "✕ COULDN'T REACH THE SCHEDULE — TRY AGAIN", true);
      void loadNight(night);
      return false;
    } finally {
      set({ schBusy: false });
    }
  };

  const loadNight = async (k: string): Promise<void> => {
    if (!k || !get().session) return;
    try {
      const m = await get().repo.schedGet(k, k);
      set({ sched: withNight(get().sched, k, m[k] ?? []) });
    } catch {
      /* transient — the next realtime nudge or visit retries */
    }
  };

  const now = new Date();

  return {
    sched: {},
    schSpan: null,
    schY: now.getFullYear(),
    schM: now.getMonth(),
    schDay: '',
    schGame: 0,
    schEdit: false,
    schNomOpen: false,
    schArm: '',
    schPick: 0,
    schFrom: null,
    schTo: null,
    schQ: '',
    schTier: 'all',
    schBusy: false,

    refreshSched: async () => {
      const s = get();
      if (!s.session) return;
      const span = scMonthSpan(s.schY, s.schM);
      const seq = ++fetchSeq;
      try {
        const m = await s.repo.schedGet(span.from, span.to);
        if (seq !== fetchSeq) return;
        // Keep nights outside the span (an open night elsewhere); replace the
        // span itself whole, so a night emptied by someone else empties here.
        const kept: SchedMap = {};
        for (const [k, v] of Object.entries(get().sched)) if (k < span.from || k > span.to) kept[k] = v;
        set({ sched: { ...kept, ...m }, schSpan: span });
      } catch {
        /* transient — the next SUBSCRIBED or visit retries */
      }
      const day = get().schDay;
      if (day && (day < span.from || day > span.to)) void loadNight(day);
    },

    schLoadNight: (k) => loadNight(k),

    schMonthGo: (d) => {
      const s = get();
      const m = s.schM + d;
      set({ schY: s.schY + Math.floor(m / 12), schM: ((m % 12) + 12) % 12 });
      void get().refreshSched();
    },

    schOpenDay: (k) => {
      get().schArmSet('');
      set({ screen: 'schedDay', schDay: k, schGame: 0, schEdit: false, schNomOpen: false, meOpen: false });
      top();
      void loadNight(k);
    },

    schBack: () => {
      get().schArmSet('');
      set({ screen: 'schedule', schGame: 0, schEdit: false, schNomOpen: false });
      top();
    },

    schNomStart: () => set({ schNomOpen: true, schPick: 0, schFrom: null, schTo: null, schQ: '', schTier: 'all' }),
    schNomClose: () => set({ schNomOpen: false, schPick: 0 }),

    schNomAdd: async () => {
      const s = get();
      const k = s.schDay;
      const id = s.schPick;
      if (!s.me() || !k || !id) return;
      if ((s.sched[k] ?? []).some((n) => n.id === id)) {
        s.toast('ALREADY NOMINATED FOR THIS NIGHT');
        return;
      }
      const g = s.gById(id);
      await write(
        k,
        () => s.repo.schedNominate(k, id, s.schFrom, s.schTo),
        () => {
          set({ schNomOpen: false, schPick: 0 });
          get().toast((g ? g.n.toUpperCase() : 'GAME') + ' → ' + scDayTxt(k));
        },
      );
    },

    schQuarter: async (id) => {
      const s = get();
      const k = s.schDay;
      if (!s.me() || !k) return;
      await write(k, () => s.repo.schedBack(k, id));
    },

    schOpenGame: (id) => {
      get().schArmSet('');
      set({ schGame: id, schEdit: false });
    },
    schCloseGame: () => {
      get().schArmSet('');
      set({ schGame: 0, schEdit: false });
    },

    schEditTgl: () => {
      const s = get();
      const n = (s.sched[s.schDay] ?? []).find((x) => x.id === s.schGame);
      set({ schEdit: !s.schEdit, schFrom: n ? n.from : null, schTo: n ? n.to : null });
    },

    schEditSave: async () => {
      const s = get();
      const k = s.schDay;
      const id = s.schGame;
      if (!s.me() || !k || !id) return;
      await write(
        k,
        () => s.repo.schedSetRange(k, id, s.schFrom, s.schTo),
        () => {
          set({ schEdit: false });
          get().toast('RANGE UPDATED');
        },
      );
    },

    schRemove: async () => {
      const s = get();
      const k = s.schDay;
      const id = s.schGame;
      if (!s.me() || !k || !id) return;
      if (s.schArm !== 'rm') {
        s.schArmSet('rm');
        return;
      }
      s.schArmSet('');
      const g = s.gById(id);
      await write(
        k,
        () => s.repo.schedRemove(k, id),
        () => {
          set({ schGame: 0, schEdit: false });
          get().toast((g ? g.n.toUpperCase() : 'GAME') + ' TAKEN OFF ' + scDayTxt(k));
        },
      );
    },

    /**
     * START GAME NIGHT: the ordinary round, opened with every nomination whose
     * window covers NOW already on the wheel (backers ride along as seconds).
     * NOW is this device's clock — the night is the room's, and the room is
     * where the button is pressed. With a round already open, the button opens
     * it (the server says so by returning it).
     */
    schStart: async () => {
      const s = get();
      const k = s.schDay;
      if (!s.me() || !k || s.rBusy) return;
      if (s.round) {
        s.rOpen();
        return;
      }
      const on = scOnWheel(s.sched[k] ?? [], new Date());
      if (!on.length) {
        s.toast('NOTHING CROSSES THE LINE YET', true);
        return;
      }
      set({ rBusy: true });
      try {
        const r = await s.repo.schedStart(
          k,
          on.map((n) => n.id),
        );
        set({ round: r, screen: 'round', rArm: '', rTier: 'all', rHov: null, rSheet: false, schGame: 0, schNomOpen: false });
        top();
        // The server answers an already-open round with that round; only a
        // round this press made is "started".
        // Counted off what the server put in the hat, not what this clock
        // offered: a game left the list, or a backer ran out of quarters.
        if (r && r.schedNight === k && r.startedBy === s.session && Date.now() - r.startedAt < 60_000) {
          get().toast('GAME NIGHT STARTED \u00B7 ' + new Set(r.noms.map((n) => n.id)).size + ' ON THE WHEEL');
        }
      } catch (e) {
        const known = e instanceof RepoError && (e.code === 'FORBIDDEN' || e.code === 'NOT_FOUND');
        get().toast(known && e.message ? '✕ ' + e.message.toUpperCase() : "✕ COULDN'T REACH GAME NIGHT — TRY AGAIN", true);
        void get().refreshRound();
      } finally {
        set({ rBusy: false });
      }
    },

    schSetPick: (schPick) => set({ schPick }),
    schSetRange: (schFrom, schTo) => set({ schFrom, schTo }),
    schSetQ: (schQ) => set({ schQ }),
    schSetTier: (schTier) => set({ schTier }),

    /** The round's arm, verbatim: 5 s, or the first click outside any `[data-arm]`. */
    schArmSet: (key) => {
      if (armTimer) clearTimeout(armTimer);
      armTimer = null;
      disarmListener();
      if (get().schArm !== key) set({ schArm: key });
      if (!key) return;
      armTimer = setTimeout(() => {
        armTimer = null;
        if (get().schArm === key) set({ schArm: '' });
      }, R_ARM_MS);
      if (typeof document !== 'undefined') {
        armAway = (ev) => {
          const t = ev.target as Element | null;
          if (t && typeof t.closest === 'function' && t.closest('[data-arm]')) return;
          if (get().schArm) set({ schArm: '' });
          disarmListener();
        };
        setTimeout(() => {
          if (armAway) document.addEventListener('click', armAway, true);
        }, 0);
      }
    },
  };
};
